import { TProfessional } from 'types/appointment';

import {
  BookableAppointment,
  BookableAppointmentState,
} from './bookableAppointments';

export type FlattenedAppointment = {
  providerGuid?: string;
  serviceGuid: string;
  bookingOrder?: number;
};

/**
 * Flattens the appointments array into an array of FlattenedAppointments
 * @param appointments the appointments to sort
 * @returns BookableAppointment[]
 */
export function flattenAppointments(
  appointments: BookableAppointment[],
): FlattenedAppointment[] {
  return appointments.reduce((acc, { providerGuid, services }) => {
    return [
      ...acc,
      ...services.map(({ guid, bookingOrder }) => ({
        providerGuid,
        serviceGuid: guid,
        bookingOrder,
      })),
    ];
  }, [] as FlattenedAppointment[]);
}

/**
 * Unflattens the appointments array into an array of BookableAppointments
 *
 * @param flattenedAppointments
 * @returns
 */
export const unflattenAppointments = (
  flattenedAppointments: FlattenedAppointment[],
): BookableAppointment[] => {
  const appointments: BookableAppointment[] = [];

  let currentAppointment: BookableAppointment | undefined;

  flattenedAppointments.forEach((flattenedAppointment) => {
    const { providerGuid, serviceGuid, bookingOrder } = flattenedAppointment;

    currentAppointment = {
      providerGuid,
      services: [{ guid: serviceGuid, bookingOrder }],
    };
    appointments.push(currentAppointment);
  });
  return appointments;
};

type sequence = {
  length: number;
  sequence: string[];
};

function longestSequence(
  arr: string[][],
  index = 0,
  lastNumber?: string,
): sequence {
  if (index === arr.length) {
    return { length: 0, sequence: [] };
  }
  let maxSequence: sequence = { length: -1, sequence: [] };

  for (const num of arr[index]) {
    const next = longestSequence(arr, index + 1, num);
    const sequenceLength = next.length + (num === lastNumber ? 1 : 0);

    if (sequenceLength > maxSequence.length) {
      maxSequence = {
        length: sequenceLength,
        sequence: [num, ...next.sequence],
      };
    }
  }

  return maxSequence;
}

/**
 * Finds all providers that can perform a specific service or service option.
 * Note: This function can return an empty array due to various reasons
 *
 * @param serviceOrOptionGUID - The GUID of the service or service option to find providers for
 * @param serviceProviders - Array of professionals from publicUser.users (available providers only)
 * @returns Array of provider GUIDs that can perform the service, or [] if none found
 */
function findProviderForService(
  serviceOrOptionGUID: string,
  serviceProviders: TProfessional[],
) {
  const providers = [];

  for (const provider of serviceProviders) {
    for (const providerService of provider.services) {
      // Check for exact service GUID match
      if (providerService.guid === serviceOrOptionGUID) {
        providers.push(provider.guid);
      } else {
        // Check if it matches a service option GUID
        const providerOption = providerService.options?.find(
          (option) => option.guid === serviceOrOptionGUID,
        );

        providerOption && providers.push(provider.guid);
      }
    }
  }
  return providers;
}

function groupAppointments(
  appointments: BookableAppointment[],
  providers: string[],
): BookableAppointment[] {
  appointments.forEach((appointment, index) => {
    if (index < providers.length) {
      appointment.auxProviderGuid = providers[index];
    }
  });

  for (let i = appointments.length - 1; i > 0; i--) {
    if (
      appointments[i].auxProviderGuid === appointments[i - 1].auxProviderGuid
    ) {
      appointments[i - 1].services.push(...appointments[i].services);
      appointments.splice(i, 1);
    }
  }

  return appointments;
}

/**
 * Optimizes appointments to minimize the number of providers needed for multi-service bookings.
 *
 * ALGORITHM:
 * 1. For each service, find all providers who can perform it using findProviderForService()
 * 2. Use longestSequence() to calculate the optimal provider assignment that minimizes total appointments
 * 3. Group services that can be performed by the same provider into consolidated appointments
 *
 * EXAMPLE:
 * Services: [Haircut, Manicure, Massage]
 * Providers: A=[Haircut, Manicure], B=[Massage], C=[Haircut, Massage]
 * Result: 2 appointments - Provider A (Haircut+Manicure), Provider B (Massage)
 *
 * EDGE CASES HANDLED:
 * - If no providers can perform a service, that service is filtered out upstream by useAvailableTimes
 * - If a service has a pre-selected provider (providerGuid), that assignment is preserved
 * - The algorithm gracefully handles services with single or multiple provider options
 */
export function optimizeAppointments(
  appointments: BookableAppointment[],
  state: BookableAppointmentState,
): BookableAppointment[] {
  const providers: string[][] = [];

  for (const appointment of appointments) {
    const appGUID = appointment.providerGuid;
    const serviceGUID = appointment.services[0].guid;

    // serviceProviders can be [] if no providers can fulfill the service
    const serviceProviders =
      appGUID === undefined
        ? findProviderForService(serviceGUID, state.serviceProviders)
        : [appGUID];

    providers.push(serviceProviders);
  }

  const optimizedProviders = longestSequence(providers).sequence;

  const groupedAppointments = groupAppointments(
    appointments,
    optimizedProviders,
  );

  return groupedAppointments.map(({ providerGuid, services }) => ({
    providerGuid,
    services: services.map(({ guid, bookingOrder }) => ({
      guid,
      bookingOrder: bookingOrder === -1 ? undefined : bookingOrder,
    })),
  }));
}
